import { lazy, Suspense, useEffect } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { CaseManagementSkeleton } from '@/features/air/case-management/components/skeletons'
import { toCaseListParams } from '@/features/air/case-management/lib/case-list-params'
import { validateCaseListSearch } from '@/features/air/case-management/lib/strict-case-list-search'
import { caseQueries } from '@/features/air/case-management/queries'
import { useCaseListSearchStore } from '@/features/air/case-management/store'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'

import { getTenantFeatures } from '@/shared/auth/getters'
import { getCurrentTenant } from '@/shared/auth/store'

import { ErrorBoundary } from '@/components/common/error-boundary'

const CaseManagementTable = lazy(
	() => import('@/features/air/case-management/components/table'),
)

export const Route = createFileRoute('/_dashboard/air/_layout/case-management')(
	{
		// Shared with the multi-tenant Cases tab. An invalid value is removed
		// from the URL and named in a toast; a filter key missing from the
		// schema is **silently stripped**.
		validateSearch: validateCaseListSearch,

		// The same params the table sends, so the prefetch warms its exact key.
		// Uncached tenant details with an Investigation filter set make it miss,
		// which only costs a request.
		// The search query is in the params, so a search link prefetches too.
		loaderDeps: ({ search }) => ({
			params: toCaseListParams(search, {
				showInvestigation: Boolean(
					getTenantFeatures()?.beta_enable_automated_investigations,
				),
			}),
		}),

		loader: ({ context: { queryClient }, deps }) => {
			const tenant = getCurrentTenant()

			if (tenant) {
				queryClient.prefetchInfiniteQuery(
					caseQueries.cases_list(tenant, 25, deps.params),
				)
			}
		},

		head: () => ({
			meta: [
				{
					title: 'Case Management | BitLyft AIR®',
				},
			],
		}),
		component: RouteComponent,
		// Same shape the list section falls back to — one definition, two moments.
		pendingComponent: CaseManagementSkeleton,
		errorComponent: RouteErrorComponent,
	},
)

function RouteComponent() {
	const search = Route.useSearch()
	const setLastListSearch = useCaseListSearchStore(
		(state) => state.setLastListSearch,
	)

	useEffect(() => {
		setLastListSearch(search)
	}, [search, setLastListSearch])

	return (
		<div className="flex h-full w-full flex-col items-start justify-start gap-6">
			<ErrorBoundary feature="case-management" section="list">
				<Suspense fallback={<CaseManagementSkeleton />}>
					<CaseManagementTable />
				</Suspense>
			</ErrorBoundary>
		</div>
	)
}
